/**
 * useAuthenticatedApi Hook
 * Synchronizes Auth0 access token with the API service
 *
 * This hook ensures the API service always has a valid token
 * by refreshing it automatically when needed.
 *
 * Also handles submitting pending terms acceptance after authentication.
 */

import { useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { useAuth0 } from '@auth0/auth0-react';
import { setAccessToken, clearAccessToken } from '../services/api';
import { buildLogoutReturnTo } from '../utils/authRedirect';
import {
  getStoredTermsAcceptance,
  submitTermsAcceptance,
} from '../services/termsService';

/**
 * Hook that keeps the API service synchronized with Auth0 tokens
 * Should be used at the app level (e.g., in AuthWrapper)
 */
export const useAuthenticatedApi = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } =
    useAuth0();

  // Track if we've already set up the token
  const tokenSetRef = useRef(false);

  // Track if we've already submitted terms acceptance
  const termsSubmittedRef = useRef(false);

  /**
   * Submit pending terms acceptance to backend
   * Called once after successful authentication
   */
  const submitPendingTerms = useCallback(async () => {
    // Only submit once per session
    if (termsSubmittedRef.current) {
      return;
    }

    // Check if there are pending terms to submit
    const pendingTerms = getStoredTermsAcceptance();
    if (!pendingTerms) {
      termsSubmittedRef.current = true; // Mark as done even if nothing to submit
      return;
    }

    try {
      await submitTermsAcceptance(pendingTerms);
      termsSubmittedRef.current = true;
    } catch (error) {
      // Don't block authentication flow on terms submission failure
      logger.error('Failed to submit terms acceptance:', error);
      // Will try again on next page load
    }
  }, []);

  /**
   * Refresh the access token and update the API service
   */
  const refreshToken = useCallback(async () => {
    if (!isAuthenticated) {
      clearAccessToken();
      tokenSetRef.current = false;
      termsSubmittedRef.current = false; // Reset on logout
      return null;
    }

    try {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
      tokenSetRef.current = true;

      // Submit any pending terms acceptance after token is set
      // This runs after the first successful authentication
      submitPendingTerms();

      return token;
    } catch (error) {
      logger.error('Failed to get access token:', error);
      clearAccessToken();
      tokenSetRef.current = false;

      // If token refresh fails completely, user needs to re-authenticate
      if (
        error.error === 'login_required' ||
        error.error === 'consent_required'
      ) {
        logout({ logoutParams: { returnTo: buildLogoutReturnTo() } });
      }

      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently, logout, submitPendingTerms]);

  /**
   * Get a fresh token for immediate use
   * Use this in components that need to ensure they have a valid token
   */
  const getToken = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
      return token;
    } catch (error) {
      logger.error('Failed to get token:', error);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Set up token when authentication state changes
  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && !tokenSetRef.current) {
        refreshToken();
      } else if (!isAuthenticated) {
        clearAccessToken();
        tokenSetRef.current = false;
      }
    }
  }, [isAuthenticated, isLoading, refreshToken]);

  // Set up periodic token refresh (every 50 minutes, tokens typically last 1 hour)
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(() => {
      refreshToken();
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, refreshToken]);

  return {
    refreshToken,
    getToken,
    isReady: !isLoading && (isAuthenticated ? tokenSetRef.current : true),
  };
};

export default useAuthenticatedApi;
